import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { Alert, Button, FullScreenLoader, Panel } from '../components/ui'
import { t } from '../i18n'
import type { Permission } from '../api/permissions'
import { useSession } from './session'

export const SIGN_IN_PATH = '/signin'

/** Where the visitor was heading before being asked to sign in. */
export interface SignInRedirectState {
  readonly from?: string
}

/**
 * Let signed-in people through; send everyone else to the sign-in screen.
 *
 * This is convenience, not protection. It decides what to render, and rendering
 * happens on a machine the visitor controls. Every endpoint behind it checks the
 * session again on the server, which is the check that counts.
 */
export function RequireAuth() {
  const { status, refresh } = useSession()
  const location = useLocation()

  if (status === 'loading') {
    return <FullScreenLoader label={t('session.checking')} />
  }

  if (status === 'unreachable') {
    // Deliberately not the sign-in screen: inviting someone to type a password
    // into a form that cannot possibly work wastes their time and hides the real
    // problem.
    return (
      <div className="centered">
        <Panel className="signin__card">
          <div className="signin__brand">
            <h1 className="signin__title">{t('session.unreachableTitle')}</h1>
            <p className="signin__subtitle">{t('session.unreachable')}</p>
          </div>
          <Button variant="secondary" block onClick={refresh}>
            {t('common.retry')}
          </Button>
        </Panel>
      </div>
    )
  }

  if (status === 'anonymous') {
    const state: SignInRedirectState = { from: location.pathname + location.search }
    return <Navigate to={SIGN_IN_PATH} state={state} replace />
  }

  return <Outlet />
}

/**
 * Hide a screen the person has no permission for.
 *
 * Same caveat as `RequireAuth`: this shapes the interface. The server answers
 * 403 either way.
 */
export function RequirePermission({
  permission,
  children,
}: {
  readonly permission: Permission
  readonly children: ReactNode
}) {
  const { can } = useSession()

  if (!can(permission)) {
    return (
      <div className="content__inner">
        <Alert tone="warning">
          <div>
            <strong>{t('errors.forbiddenTitle')}</strong>
            <div>{t('errors.forbiddenBody', { permission })}</div>
          </div>
        </Alert>
      </div>
    )
  }

  return <>{children}</>
}
